/**
 * AssistantConsentCard — inline assistant consent, captured at the point of use.
 *
 * The Settings → Assistant toggle stays the canonical manage / withdraw surface.
 * This card is a SECOND capture location for the same consent: when a user
 * initiates an assistant action while opted out, the card asks in place instead
 * of pointing them out of the flow. Same copy, same lawful basis, same
 * withdrawal path (DPIA delta §5 + Amendment 2026-08-21,
 * `.plan/legal/2026-07-dpia-workers-ai-assistant.md`).
 *
 * Consent-display rules (carried over verbatim from AssistantSection.tsx —
 * binding):
 *   - The displayed state reflects the SERVER-confirmed value only. Nothing is
 *     flipped optimistically; the card resolves solely after the save succeeds
 *     (the parent stops rendering it once the confirmed state says opted in).
 *   - Rule 8: a failed save surfaces a visible inline error (brand voice, no
 *     exclamations) and leaves the state exactly where it was.
 *   - `optIn === null` = state unavailable (still loading, or a read failure).
 *     The consent action is withheld and a quiet line renders — never a
 *     fabricated value, and never a consent act on an unknown state.
 *
 * It is the caller's job to render this ONLY on a user-initiated assistant
 * surface, and only when the environment kill switch is on and the user is
 * opted out. There is no unsolicited render on library / editor load, and
 * "Not now" is a real answer — it dismisses the card for that surface instance
 * and leaves the opted-out state untouched (no RPC).
 *
 * Design refs:
 *   design.md §2 — one accent, reserved for signal. The host surface's primary
 *     CTA (e.g. the paste pane's footer Convert) owns the single accent, so the
 *     consent action here is a bordered quiet-primary, not a second accent fill.
 *     Hairline border + whitespace for structure; no shadow, no fill.
 *   design.md §4 — Geist sans for prose; no mono (this is prose, not data).
 *   Rule 7 — 375 / 768 / 1280: the card is fluid, the action row wraps, both
 *     affordances clear the 44px tap-target floor.
 *   branding.md §5, §5.2.1 — precise, quiet, unsentimental; no exclamations, no
 *     banned words.
 */

import { useState, type CSSProperties } from "react";
import {
  ASSISTANT_CONSENT_BODY,
  ASSISTANT_CONSENT_SAVE_ERROR,
  ASSISTANT_CONSENT_UNAVAILABLE,
} from "./consentCopy.js";

// ── Copy (voice-reviewed; branding.md §5.2.1) ────────────────────────────────

/** The consent action. Names the act plainly — no urgency, no persuasion. */
const ACCEPT_LABEL = "Turn on the assistant";
/** In-flight label for the consent action (matches the app's "…" idiom). */
const ACCEPT_SAVING_LABEL = "Turning on…";
/** The decline. An equal, unpenalised answer — not a dismissal glyph. */
const DECLINE_LABEL = "Not now";
/** Where this lives afterwards, so the card is not the only way back to it. */
const MANAGE_CAPTION = "You can change this later in Settings → Assistant.";

// ── Styles ───────────────────────────────────────────────────────────────────

// No local :focus-visible rule: tokens.css already applies the accent focus ring
// globally (`:focus-visible { outline: 2px solid var(--accent); outline-offset:
// 2px }`), so both buttons get the sanctioned ring for free.

// Hairline block, no fill and no shadow — structure from the 1px border and
// whitespace alone (design.md §2).
const cardStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-2)",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius-card)",
  // Matches the host pane's own --space-4 padding: a nested block should never
  // read tighter than the container it sits in.
  padding: "var(--space-4)",
  // Rule 7: never wider than its column at 375px, and long words break rather
  // than push the modal into a horizontal scroll.
  maxWidth: "100%",
  boxSizing: "border-box",
  overflowWrap: "break-word",
};

const contextStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  lineHeight: 1.5,
  color: "var(--text)",
  margin: 0,
};

// 13px, matching the canonical Settings → Assistant rendering of this same
// string. The consent copy is legally load-bearing (DPIA delta §5) and must not
// render smaller here than it does there, nor smaller than its own context line.
const bodyStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  lineHeight: 1.5,
  color: "var(--muted)",
  margin: 0,
};

const actionsStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "var(--space-2)",
  marginTop: "var(--space-1)",
};

/**
 * The consent action. Deliberately NOT accent-filled: the host surface's own
 * primary CTA holds the single accent (design.md §2 principle 6), and a second
 * accent-weight control in the same panel would compete with it. A bordered
 * quiet-primary — functional hairline + full-strength text — reads as the
 * primary of this card without claiming the surface's accent.
 */
const acceptStyle = (saving: boolean): CSSProperties => ({
  background: "none",
  border: "1px solid var(--line-strong)",
  borderRadius: "var(--radius-button)",
  padding: "var(--space-2) var(--space-4)",
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  color: saving ? "var(--muted)" : "var(--text)",
  cursor: saving ? "default" : "pointer",
  minHeight: "var(--tap-target-min)",
  // Sized to the LONGER of the two labels ("Turn on the assistant" vs
  // "Turning on…") so the row does not jerk when the label swaps mid-save.
  // Quiet motion means no layout shift either (design.md §2).
  minWidth: "13.5em",
  transition: "color var(--motion-state), border-color var(--motion-state)",
});

/** The decline — quieter still, but a full tap target, never a bare glyph. */
const declineStyle = (saving: boolean): CSSProperties => ({
  background: "none",
  border: "1px solid transparent",
  borderRadius: "var(--radius-button)",
  padding: "var(--space-2) var(--space-3)",
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  // Mirrors the accept treatment while a save is in flight: a disabled control
  // must not keep an interactive cursor or its full-strength colour.
  color: saving ? "var(--faint)" : "var(--muted)",
  cursor: saving ? "default" : "pointer",
  minHeight: "var(--tap-target-min)",
  transition: "color var(--motion-state)",
});

const captionStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  lineHeight: 1.4,
  color: "var(--muted)",
  margin: 0,
};

const errorStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  lineHeight: 1.4,
  color: "var(--del)",
  margin: 0,
};

// ── Props ────────────────────────────────────────────────────────────────────

export interface AssistantConsentCardProps {
  /**
   * The per-surface first line: what the assistant would do HERE. Kept as a
   * prop so each surface states its own action while the consent body below it
   * stays identical everywhere.
   */
  context: string;
  /**
   * The server-confirmed opt-in state, or `null` when unavailable (still
   * loading or a read failure). Never an optimistic value. `true` renders
   * nothing — consent is already given and the host surface should have stopped
   * rendering the card.
   */
  optIn: boolean | null;
  /**
   * Persist the consent. MUST throw on failure (Rule 8) so this card can
   * surface the error and leave the state unchanged. Resolving means the server
   * confirmed the opt-in.
   */
  onConsent: () => Promise<void>;
  /**
   * The user declined. Dismisses the card for this surface instance; no RPC,
   * no state change.
   */
  onDecline: () => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export function AssistantConsentCard({
  context,
  optIn,
  onConsent,
  onDecline,
}: AssistantConsentCardProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Consent already given — the host surface owns the transition out of this
  // card; rendering a consent ask over a granted consent would be wrong.
  if (optIn === true) return null;

  const unavailable = optIn === null;

  async function handleConsent() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onConsent();
      // No setState on success: the confirmed state flows down from the host,
      // which stops rendering this card. Nothing is flipped locally.
    } catch (err) {
      // Rule 8: surface the failure; the opted-out state is untouched.
      console.error({ event: "assistant_consent_save_failed", err });
      setError(ASSISTANT_CONSENT_SAVE_ERROR);
      setSaving(false);
    }
  }

  return (
    // role="status" keeps parity with the quiet opted-out line this card
    // replaced (a role="status" span): the state change is announced rather than
    // silently inserted. The save error below keeps its own role="alert" — an
    // assertive nested region, because a failed consent write is not a passive
    // status update.
    <div style={cardStyle} role="status" data-testid="assistant-consent-card">
      <p style={contextStyle} data-testid="assistant-consent-context">
        {context}
      </p>
      {/* data-testid is shared with Settings → Assistant so one test can assert
          both consent surfaces render the identical string. */}
      <p style={bodyStyle} data-testid="assistant-consent-body">
        {ASSISTANT_CONSENT_BODY}
      </p>

      {unavailable ? (
        <p style={bodyStyle} role="status" data-testid="assistant-consent-unavailable">
          {ASSISTANT_CONSENT_UNAVAILABLE}
        </p>
      ) : (
        <>
          <div style={actionsStyle}>
            <button
              type="button"
              style={acceptStyle(saving)}
              onClick={() => void handleConsent()}
              disabled={saving}
              data-testid="assistant-consent-accept"
            >
              {saving ? ACCEPT_SAVING_LABEL : ACCEPT_LABEL}
            </button>
            <button
              type="button"
              style={declineStyle(saving)}
              onClick={onDecline}
              disabled={saving}
              data-testid="assistant-consent-decline"
            >
              {DECLINE_LABEL}
            </button>
          </div>
          <p style={captionStyle}>{MANAGE_CAPTION}</p>
        </>
      )}

      {error !== null && (
        <p style={errorStyle} role="alert" data-testid="assistant-consent-error">
          {error}
        </p>
      )}
    </div>
  );
}
